import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { COLORS, FONTS, SPRING, STAKE } from "./constants";
import { Backdrop, SceneTitle, glow } from "./shared";

const SOURCES = [
  { icon: "📦", c: COLORS.neonPurple, name: "GitHub" },
  { icon: "📘", c: COLORS.neonBlue,   name: "Confluence" },
  { icon: "💬", c: COLORS.neonPink,   name: "Slack" },
  { icon: "📋", c: COLORS.neonOrange, name: "Jira" },
  { icon: "📁", c: COLORS.neonYellow, name: "Drive" },
  { icon: "📄", c: COLORS.neonCyan,   name: "PDFs" },
];

export const Scene07WorkerSolve: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const fade = interpolate(frame, [durationInFrames - 30, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  const titleAp = interpolate(spring({ frame, fps, config: SPRING.snappy }), [0, 1], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fade, overflow: "hidden" }}>
      <Backdrop frame={frame} accent={STAKE.success} />

      <SceneTitle
        text="One chat. All your sources."
        subtitle="Unified search. Cited answers. No context-switching."
        color={COLORS.neonGreen}
        frame={frame}
        appear={titleAp}
      />

      {/* Left: source icons in column */}
      <div style={{
        position: "absolute", top: 280, left: 120,
        display: "flex", flexDirection: "column", gap: 22,
      }}>
        {SOURCES.map((s, i) => {
          const sp = spring({ frame: frame - 20 - i * 8, fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const tx = interpolate(sp, [0, 1], [-100, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          return (
            <div key={s.name} style={{
              width: 320, height: 76,
              opacity: op,
              transform: `translateX(${tx}px)`,
              background: `linear-gradient(135deg, ${s.c}18, ${COLORS.bgCard}ee)`,
              border: `2px solid ${s.c}80`,
              borderLeft: `5px solid ${s.c}`,
              borderRadius: 14,
              display: "flex",
              alignItems: "center",
              gap: 20,
              padding: "0 24px",
              boxShadow: `0 0 25px ${s.c}44`,
            }}>
              <div style={{ fontSize: 42 }}>{s.icon}</div>
              <div style={{
                fontSize: 26,
                fontFamily: FONTS.heading,
                fontWeight: 700,
                color: s.c,
                textShadow: glow(s.c),
              }}>{s.name}</div>
            </div>
          );
        })}
      </div>

      {/* Flow arrows */}
      <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={1920} height={1080}>
        {SOURCES.map((s, i) => {
          const startY = 280 + i * 98 + 38;
          const sp = spring({ frame: frame - 90 - i * 6, fps, config: SPRING.gentle });
          const op = interpolate(sp, [0, 1], [0, 0.8], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const dashOff = -(frame * 3) % 16;
          return (
            <line
              key={i}
              x1={440} y1={startY}
              x2={1000} y2={540}
              stroke={s.c}
              strokeWidth={3}
              strokeOpacity={op}
              strokeDasharray="10 6"
              strokeDashoffset={dashOff}
              style={{ filter: `drop-shadow(0 0 6px ${s.c})` }}
            />
          );
        })}
      </svg>

      {/* Center: EAKC brain */}
      <div style={{
        position: "absolute",
        top: 430, left: 920,
        width: 220, height: 220,
      }}>
        <div style={{
          width: "100%", height: "100%",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${COLORS.neonGreen}66, ${COLORS.neonGreen}15 60%, transparent)`,
          border: `3px solid ${COLORS.neonGreen}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 110,
          boxShadow: `0 0 80px ${COLORS.neonGreen}88, inset 0 0 40px ${COLORS.neonGreen}33`,
          transform: `scale(${1 + Math.sin(frame * 0.05) * 0.03})`,
        }}>
          🧠
        </div>
      </div>

      {/* Right: Cited answer card */}
      {(() => {
        const sp = spring({ frame: frame - 160, fps, config: SPRING.gentle });
        const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
        const tx = interpolate(sp, [0, 1], [100, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
        return (
          <div style={{
            position: "absolute",
            top: 280, right: 100,
            width: 560,
            opacity: op,
            transform: `translateX(${tx}px)`,
            background: `linear-gradient(135deg, ${COLORS.neonGreen}12, ${COLORS.bgCard}ee)`,
            border: `2px solid ${COLORS.neonGreen}`,
            borderRadius: 18,
            padding: 30,
            boxShadow: `0 0 40px ${COLORS.neonGreen}55`,
          }}>
            <div style={{
              fontSize: 14,
              fontFamily: FONTS.mono,
              color: COLORS.textMuted,
              marginBottom: 10,
              letterSpacing: 1.5,
              textTransform: "uppercase",
            }}>Unified Answer</div>
            <div style={{
              fontSize: 24,
              fontFamily: FONTS.body,
              color: COLORS.textPrimary,
              lineHeight: 1.45,
              marginBottom: 20,
            }}>
              Batch analysis uses a worker pool pattern defined in
              <span style={{ color: COLORS.neonCyan, fontFamily: FONTS.mono }}> batch_runner.py:42</span>,
              coordinated via Redis streams per the Jan-14 architecture thread.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["github:batch_runner.py", "slack:#arch 2026-01-14", "confluence:/batch-design"].map((cite, i) => (
                <div key={i} style={{
                  fontSize: 13,
                  fontFamily: FONTS.mono,
                  color: COLORS.neonCyan,
                  padding: "6px 14px",
                  borderRadius: 10,
                  background: `${COLORS.neonCyan}10`,
                  border: `1px solid ${COLORS.neonCyan}55`,
                }}>
                  [{i + 1}] {cite}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Bottom stat */}
      <div style={{
        position: "absolute", bottom: 60, left: 0, right: 0,
        textAlign: "center",
        opacity: interpolate(spring({ frame: frame - 220, fps, config: SPRING.gentle }), [0, 1], [0, 1], {
          extrapolateLeft: "clamp", extrapolateRight: "clamp",
        }),
      }}>
        <div style={{
          fontSize: 40,
          fontFamily: FONTS.heading,
          fontWeight: 800,
          color: COLORS.neonGreen,
          textShadow: glow(COLORS.neonGreen, true),
        }}>
          72% retrieval accuracy · vs 8% on raw keyword search
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
